import React, { useState } from "react";
import { motion } from "framer-motion";
import { Users, Trophy, Calendar, Heart, ArrowRight, Check, MapPin, Mail, Phone as PhoneIcon, Award, Target } from "lucide-react";
import { TornEdge } from "../../components/sport/TornEdge";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const CATEGORIES = [
  { age: "Babyhand", years: "3-5 ans", description: "Découverte ludique du handball par le jeu, motricité globale.", icon: "🧸", price: 80 },
  { age: "Mini-Hand", years: "6-7 ans", description: "Premier contact avec le ballon, règles simplifiées, plaisir avant tout.", icon: "⚽", price: 100 },
  { age: "U9", years: "8-9 ans", description: "Apprentissage des fondamentaux, premier championnat dépanrtemental.", icon: "🤾", price: 130 },
  { age: "U11", years: "10-11 ans", description: "Perfectionnement technique, championnat régional.", icon: "🤾", price: 150 },
  { age: "U13", years: "12-13 ans", description: "Préformation, sélections départementales possibles.", icon: "🏆", price: 170 },
  { age: "U15", years: "14-15 ans", description: "Formation, accès aux sélections régionales/nationales.", icon: "🏆", price: 180 },
  { age: "U17", years: "16-17 ans", description: "Pré-élite, championnat national possible. Pôle espoirs.", icon: "🥇", price: 200 },
  { age: "U19", years: "18-19 ans", description: "Élite, intégration au centre de formation, pré-pro.", icon: "🥇", price: 220 },
  { age: "Loisir Adulte", years: "+18 ans", description: "Hand pour le plaisir, entraînements en soirée, compétitions amicales.", icon: "🎉", price: 180 },
];

const VALUES = [
  { icon: Heart, title: "Passion", text: "Le handball comme art de vivre, partagé par tous." },
  { icon: Users, title: "Famille", text: "Un club à taille humaine où chacun trouve sa place." },
  { icon: Trophy, title: "Excellence", text: "Formation de qualité, encadrement diplômé d'État." },
  { icon: Target, title: "Engagement", text: "Valeurs fortes : respect, fair-play, dépassement." },
];

export default function SportRejoindre() {
  const [step, setStep] = useState(0); // 0 = info, 1 = form
  const [form, setForm] = useState({
    category: "",
    firstname: "",
    lastname: "",
    birthdate: "",
    parent_name: "",
    email: "",
    phone: "",
    address: "",
    medical_certificate: false,
    licence_existing: false,
  });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.medical_certificate) {
      toast.error("Le certificat médical est obligatoire pour la pratique");
      return;
    }
    setSent(true);
    toast.success("Demande de pré-inscription envoyée !");
  };

  return (
    <div style={{ background: BG_DARK }}>
      {/* HERO */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-pink-500 text-black mb-4 shadow-2xl">
            <Users size={36}/>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
            École de handball · Saison 2025-2026
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Rejoignez <span style={{ color: SECONDARY }}>le club</span>
          </motion.h1>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">De 3 à 99 ans, l'IPH accueille tous les amoureux du handball. Découvrez nos catégories et lancez votre pré-inscription.</p>
        </div>
        <TornEdge position="bottom" color="#fbf7fa" variant="shred" height={56}/>
      </section>

      {/* VALUES */}
      <section className="py-16" style={{ background: "#fbf7fa" }}>
        <div className="max-w-5xl mx-auto px-4 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUES.map((v, i) => (
            <motion.div key={v.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl p-5 bg-white shadow-lg hover:shadow-xl border border-slate-100 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full text-white mb-3"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
                <v.icon size={20}/>
              </div>
              <h3 className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{v.title}</h3>
              <p className="text-slate-500 text-xs mt-1">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="py-16" style={{ background: "#fbf7fa" }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Catégories</div>
            <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
              Choisissez votre <span style={{ color: SECONDARY }}>équipe</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((c, i) => (
              <motion.button key={c.age}
                onClick={() => { setForm({ ...form, category: c.age }); setStep(1); document.getElementById("inscription")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`cat-${c.age.toLowerCase().replace(/\s/g, "-")}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="text-left rounded-2xl p-5 bg-white shadow-lg hover:shadow-2xl border border-slate-100 hover:border-pink-500 transition hover:-translate-y-1 group">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-3xl" aria-hidden>{c.icon}</span>
                  <span className="text-pink-600 font-black text-sm">{c.price}€/an</span>
                </div>
                <h3 className="font-black uppercase text-base" style={{ color: PRIMARY }}>{c.age}</h3>
                <div className="text-pink-600 text-[11px] font-black tracking-widest uppercase mt-0.5">{c.years}</div>
                <p className="text-slate-500 text-xs mt-2">{c.description}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-pink-600 group-hover:gap-2.5 transition">
                  Pré-inscrire <ArrowRight size={12}/>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="inscription" className="py-20" style={{ background: BG_DEEP }}>
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2">Pré-inscription</div>
            <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
              Lancez votre <span style={{ color: SECONDARY }}>aventure</span>
            </h2>
          </div>

          {sent ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-white p-10 text-center shadow-2xl">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 text-white mb-4">
                <Check size={32}/>
              </div>
              <h3 className="font-black text-2xl uppercase italic" style={{ color: PRIMARY }}>Pré-inscription enregistrée !</h3>
              <p className="text-slate-600 mt-3">Notre équipe vous contacte sous 7 jours pour finaliser votre inscription, valider le certificat médical et vous communiquer le planning des entraînements.</p>
              <button onClick={() => { setSent(false); setStep(0); setForm({ ...form, firstname: "", lastname: "", email: "", phone: "" }); }}
                className="mt-5 px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 border-slate-300 text-slate-600 hover:bg-slate-100">
                Inscrire un autre joueur
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-3xl bg-white shadow-2xl p-6 sm:p-10 space-y-4" data-testid="rejoindre-form">
              <div>
                <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Catégorie *</label>
                <select data-testid="form-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none">
                  <option value="">— Sélectionnez —</option>
                  {CATEGORIES.map((c) => <option key={c.age} value={c.age}>{c.age} ({c.years}) — {c.price}€</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Prénom *</label>
                  <input data-testid="form-firstname" required value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Nom *</label>
                  <input data-testid="form-lastname" required value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Date de naissance *</label>
                  <input data-testid="form-birthdate" type="date" required value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Parent (si mineur)</label>
                  <input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Email *</label>
                  <input data-testid="form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
                <div>
                  <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Téléphone *</label>
                  <input data-testid="form-phone" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                </div>
              </div>
              <div>
                <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Adresse</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1 w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
              </div>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input data-testid="form-medical" type="checkbox" required checked={form.medical_certificate} onChange={(e) => setForm({ ...form, medical_certificate: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-pink-500"/>
                <span className="text-sm text-slate-600 group-hover:text-slate-900">
                  J'atteste pouvoir fournir un <strong>certificat médical d'aptitude au handball</strong> de moins de 6 mois (obligatoire pour la pratique).
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={form.licence_existing} onChange={(e) => setForm({ ...form, licence_existing: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-pink-500"/>
                <span className="text-sm text-slate-600">J'ai déjà été licencié à la FFHandball (mutation possible)</span>
              </label>
              <button data-testid="form-submit" className="w-full mt-2 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-black uppercase text-sm tracking-widest shadow-2xl hover:scale-[1.02] transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
                Envoyer ma pré-inscription <ArrowRight size={16}/>
              </button>
              <p className="text-center text-[11px] text-slate-400">Vos données ne sont utilisées que pour le traitement de votre demande.</p>
            </form>
          )}
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-16 text-center" style={{ background: BG_DARK }}>
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <h3 className="text-white font-black text-2xl uppercase italic mb-4">Une question ?</h3>
          <p className="text-white/70 mb-6">Contactez le secrétariat du club, nous serons ravis de vous recevoir.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:contact@istreshandball.com" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-pink-500 text-black font-black uppercase text-xs tracking-widest hover:scale-105 transition">
              <Mail size={14}/> contact@istreshandball.com
            </a>
            <a href="tel:+33442561234" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-white/30 text-white font-black uppercase text-xs tracking-widest hover:bg-white hover:text-black transition">
              <PhoneIcon size={14}/> 04 42 56 12 34
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
